import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Button,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  FormControlLabel,
  MenuItem,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogContentText,
  useMediaQuery,
  FormGroup
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadImage } from "../../../Components/UploadFile/upload";
import CloseIcon from "@mui/icons-material/Close";
import {
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumberForDisplay,
} from "../../../Components/Validation/validationUtils";
import {
  useCreateTeamMutation,
  useDeleteEmployeeScheduleMutation,
  useGetDepartmentQuery,
  useGetPositionQuery,
} from "../../../Api/attoDeskApi";
import AddIcon from "@mui/icons-material/Add";
import {
  IPosition,
  ITeam,
  ITeamCreate,
} from "../../../Api/Interface/api.interface";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import NewDepartement from "../../Department/Edit/index";
import { IDepartment } from "../../../Api/Interface/api.interface";
import Delete from "@mui/icons-material/Delete";
import NewPosition from "../../Position/Edit/index";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import useIsMobile from "../../../Core/hook/useIsMobile";

interface TimeSlot {
  id: number;
  startTime: string;
  endTime: string;
  hourlySalary: string;
}

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ITeam;
}

const toAllCaps = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.toUpperCase();
};

const Team = ({ openModel = false, handleCloseDialog, data }: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [newCompany, { isLoading }] = useCreateTeamMutation();
  const [showPassword, setShowPassword] = useState(false);
  const isMobileRes = useIsMobile();

  const [openDialog, setOpenDialog] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<{
    index: number;
    slotIndex: number;
  } | null>(null);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const { data: departmentData, isLoading: departmentLoading } =
    useGetDepartmentQuery({
      searchText: "",
    });

  const { data: positionData, isLoading: positionLoading } =
    useGetPositionQuery({
      searchText: "",
    });

  const [openDepartment, setOpenDepartment] = useState(false);
  const [openPosition, setOpenPosition] = useState(false);

  const departmentList = useMemo(() => {
    return departmentData?.data as IDepartment[];
  }, [departmentData?.data]);

  const positionList = useMemo(() => {
    return positionData?.data as IPosition[];
  }, [positionData?.data]);

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik<ITeamCreate>({
    initialValues: {
      id: String(data?.id) || "",
      teamId: data?.teamId || "",
      teamPositionId: String(data?.teamPositionId) || "",
      teamHourlySalary: data?.teamHourlySalary || "",
      teamOtHourlySalary: data?.teamOtHourlySalary || "",
      teamDepartmentId: String(data?.teamDepartmentId) || "",
      teamMobileNum: data?.teamMobileNum
        ? formatPhoneNumberForDisplay(data.teamMobileNum)
        : "",
      teamFirstName: data?.teamFirstName || "",
      teamLastName: data?.teamLastName || "",
      teamAltMobileNum: data?.teamAltMobileNum
        ? formatPhoneNumberForDisplay(data.teamAltMobileNum)
        : "",
      teamImg: data?.teamImg || "",
      teamStreet: data?.teamStreet || "",
      teamCity: data?.teamCity || "",
      teamProvince: data?.teamProvince || "",
      teamPostalCode: data?.teamPostalCode || "",
      teamAddress: data?.teamAddress || "",
      teamEmail: data?.teamEmail || "",
      teamUserName: data?.teamUserName || "",
      teamPassWord: data?.teamPassWord || "",
      teamDescription: data?.teamDescription || "",
      emailReminder: Boolean(data?.emailReminder),
      smsReminder: Boolean(data?.smsReminder),
      availability: data?.availability?.map((available) => ({
        day: available.day,
        timeSlots: available.timeSlots?.map((slot) => ({
          id: slot.id,
          startTime: slot.startTime || "",
          endTime: slot.endTime || "",
          hourlySalary: slot.hourlySalary || "",
          spansNextDay: slot.spansNextDay || false,
        })) || [
          {
            id: null,
            startTime: "",
            endTime: "",
            hourlySalary: "",
            spansNextDay: false,
          },
        ],
      })) || [
        {
          day: "Sunday",
          timeSlots: [],
        },
        {
          day: "Monday",
          timeSlots: [],
        },
        {
          day: "Tuesday",
          timeSlots: [],
        },
        {
          day: "Wednesday",
          timeSlots: [],
        },
        {
          day: "Thursday",
          timeSlots: [],
        },
        {
          day: "Friday",
          timeSlots: [],
        },
        {
          day: "Saturday",
          timeSlots: [],
        },
      ],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const emailError = validateEmail(values.teamEmail);
      if (emailError) {
        errors.teamEmail = emailError;
      }

      const mobileNumberError = validatePhoneNumber(values.teamMobileNum);
      if (mobileNumberError) {
        errors.teamMobileNum = mobileNumberError;
      }

      const altMobileNumberError = validatePhoneNumber(values.teamAltMobileNum);
      if (altMobileNumberError) {
        errors.teamAltMobileNum = altMobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let teamImg: string | undefined = data?.teamImg;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("teamImg", selectedImage);
          teamImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const updatedData = {
          id: Number(values.id),
          teamId: values.teamId,
          teamPositionId: Number(values.teamPositionId),
          teamHourlySalary: values.teamHourlySalary,
          teamOtHourlySalary: values.teamOtHourlySalary,
          teamDepartmentId: Number(values.teamDepartmentId),
          teamMobileNum: values.teamMobileNum.replace(/-/g, ""),
          teamAltMobileNum: values.teamAltMobileNum.replace(/-/g, ""),
          teamFirstName: toProperCase(values.teamFirstName),
          teamLastName: toProperCase(values.teamLastName),
          teamCity: toProperCase(values.teamCity),
          teamAddress: values.teamAddress,
          teamEmail: values.teamEmail?.trim().toLowerCase(),
          teamDescription: values.teamDescription,
          teamImg,
          availability: values.availability,
          teamUserName: values.teamUserName,
          teamPassWord: values.teamPassWord,
          teamStreet: toProperCase(values.teamStreet),
          teamProvince: toProperCase(values.teamProvince),
          teamPostalCode: toAllCaps(values.teamPostalCode),
          emailReminder: values.emailReminder,
          smsReminder: values.smsReminder,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
          setSelectedImage(undefined);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleDeleteClick = (index: number, slotIndex: number) => {
    setSlotToDelete({ index, slotIndex });
    setOpenDialog(true);
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSlotToDelete(null);
  };

  const formValid = useMemo(() => {
    return (formik.values.teamFirstName || formik.values.teamLastName) === "" ||
      (formik.values.teamFirstName || formik.values.teamLastName) === undefined
      ? false
      : true;
  }, [formik]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const [deleteCategory] = useDeleteEmployeeScheduleMutation();

  const handleDeleteConfirm = async () => {
    if (slotToDelete) {
      const { index, slotIndex } = slotToDelete;
      const slotId = formik.values.availability[index].timeSlots[slotIndex].id;

      try {
        await deleteCategory(String(slotId));

        // Explicitly type timeSlots as TimeSlot[]
        const timeSlots = formik.values.availability[index]
          .timeSlots as TimeSlot[];

        // Filter out the deleted time slot, considering the possibility of `null` id
        formik.setFieldValue(
          `availability.${index}.timeSlots`,
          timeSlots.filter((slot, i) => i !== slotIndex) // Ensure that we remove the right slot by index
        );

        setOpenDialog(false); // Close dialog after deletion
      } catch (error) {
        console.error("Error deleting time slot:", error);
      }
    }
  };

  if (departmentLoading || positionLoading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-describedby="alert-dialog-description"
        classes={{ paper: "inherit" }}
        fullScreen={isMobileRes}
        fullWidth={isMobileRes}
        maxWidth="lg"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="lg"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid item pt={2} alignContent={"center"}>
                  <Typography className={classes.cardTitle}>
                    {data ? "Edit" : "Add"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  pt={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Add Staff Image
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} p={"24px"}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Box className={classes.imgBox}>
                            {selectedImage && (
                              <img
                                src={URL.createObjectURL(selectedImage)}
                                alt="Uploaded"
                                className={classes.SelectImageBox}
                              />
                            )}
                            <div>
                              <input
                                accept="image/*"
                                style={{ display: "none" }}
                                id="icon-button-file-1"
                                type="file"
                                onChange={handleImageChange}
                              />
                              <div
                                style={{
                                  textAlign: "center",
                                }}
                              >
                                <label htmlFor="icon-button-file-1">
                                  <IconButton
                                    color="primary"
                                    aria-label="upload picture"
                                    component="span"
                                  >
                                    <CloudUploadIcon sx={{ fontSize: 45 }} />
                                  </IconButton>
                                </label>
                              </div>
                            </div>
                          </Box>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Staff Information
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={2}>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                First Name{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="First Name"
                                size="small"
                                {...formik.getFieldProps("teamFirstName")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Last Name{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Last Name"
                                size="small"
                                {...formik.getFieldProps("teamLastName")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Mobile Number
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Mobile Number"
                                size="small"
                                value={formik.values.teamMobileNum}
                                onChange={(e) =>
                                  handlePhoneChange(
                                    "teamMobileNum",
                                    e.target.value,
                                    formik.setFieldValue
                                  )
                                }
                                onBlur={formik.handleBlur}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                error={
                                  formik.touched.teamMobileNum &&
                                  Boolean(formik.errors.teamMobileNum)
                                }
                                helperText={
                                  formik.touched.teamMobileNum &&
                                  formik.errors.teamMobileNum
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Alt Mobile Number
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Alt Mobile Number"
                                size="small"
                                value={formik.values.teamAltMobileNum}
                                onChange={(e) =>
                                  handlePhoneChange(
                                    "teamAltMobileNum",
                                    e.target.value,
                                    formik.setFieldValue
                                  )
                                }
                                onBlur={formik.handleBlur}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                error={
                                  formik.touched.teamAltMobileNum &&
                                  Boolean(formik.errors.teamAltMobileNum)
                                }
                                helperText={
                                  formik.touched.teamAltMobileNum &&
                                  formik.errors.teamAltMobileNum
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                E-mail
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="E-mail"
                                size="small"
                                type="email"
                                {...formik.getFieldProps("teamEmail")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                error={
                                  formik.touched.teamEmail &&
                                  Boolean(formik.errors.teamEmail)
                                }
                                helperText={
                                  formik.touched.teamEmail &&
                                  formik.errors.teamEmail
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">City</Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="City"
                                size="small"
                                {...formik.getFieldProps("teamCity")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid> */}
                        {/* <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Address
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Address"
                                size="small"
                                multiline
                                rows={3}
                                {...formik.getFieldProps("teamAddress")}
                                className={classes.discriptionTextField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid> */}
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                 Reminder
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={formik.values.emailReminder}
                                      onChange={(e) =>
                                        formik.setFieldValue(
                                          "emailReminder",
                                          e.target.checked
                                        )
                                      }
                                      size="small"
                                    />
                                  }
                                  label="Email"
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={formik.values.smsReminder}
                                      onChange={(e) =>
                                        formik.setFieldValue(
                                          "smsReminder",
                                          e.target.checked
                                        )
                                      }
                                      size="small"
                                    />
                                  }
                                  label="SMS"
                                />
                              </FormGroup>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Description
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Description .."
                                size="small"
                                multiline
                                rows={8}
                                {...formik.getFieldProps("teamDescription")}
                                className={classes.discriptionTextField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Address Details
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Street
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Street "
                                size="small"
                                {...formik.getFieldProps("teamStreet")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={4} md={4} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">City</Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="City"
                                size="small"
                                {...formik.getFieldProps("teamCity")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={4} md={4} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                State/Province
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="State/Province"
                                size="small"
                                {...formik.getFieldProps("teamProvince")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={4} md={4} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Postal/Zip Code
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Postal/Zip Code"
                                size="small"
                                type="email"
                                {...formik.getFieldProps("teamPostalCode")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Office Information
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container spacing={3}>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Staff Id
                                  </Typography>
                                </Grid>
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <TextField
                                    placeholder="Staff Id"
                                    size="small"
                                    {...formik.getFieldProps("teamId")}
                                    className={classes.textField}
                                    InputProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        fontSize: 14,
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Hourly Salary
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Hourly Salary"
                                size="small"
                                {...formik.getFieldProps("teamHourlySalary")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                OT Hourly Salary
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="OT Hourly Salary"
                                size="small"
                                {...formik.getFieldProps("teamOtHourlySalary")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                User Name
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="User Name"
                                size="small"
                                {...formik.getFieldProps("teamUserName")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                error={
                                  formik.touched.teamEmail &&
                                  Boolean(formik.errors.teamEmail)
                                }
                                helperText={
                                  formik.touched.teamEmail &&
                                  formik.errors.teamEmail
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Password
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Password"
                                size="small"
                                type={showPassword ? "text" : "password"}
                                {...formik.getFieldProps("teamPassWord")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={handleClickShowPassword}
                                        edge="end"
                                      >
                                        {showPassword ? (
                                          <Visibility />
                                        ) : (
                                          <VisibilityOff />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid> */}
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Department
                              </Typography>
                            </Grid>
                            <Grid item lg={10} md={10} sm={10} xs={10}>
                              <TextField
                                select
                                size="small"
                                className={classes.textField}
                                defaultValue=""
                                InputLabelProps={{ shrink: true }}
                                {...formik.getFieldProps("teamDepartmentId")}
                              >
                                <MenuItem
                                  value=""
                                  disabled
                                  style={{ color: "gray" }}
                                >
                                  Select an option
                                </MenuItem>
                                {departmentList &&
                                  departmentList.map((department) => (
                                    <MenuItem
                                      key={department.id}
                                      value={department.id}
                                    >
                                      {department.departmentName}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>
                            <Grid
                              item
                              lg={2}
                              md={2}
                              sm={2}
                              xs={2}
                              display="flex"
                              sx={{
                                justifyContent: "flex-end",
                                alignItems: "flex-end",
                                textAlign: "end",
                              }}
                            >
                              <Button
                                variant="contained"
                                className={classes.editButton}
                                onClick={() => {
                                  setOpenDepartment(true);
                                }}
                              >
                                <AddIcon />
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Position
                              </Typography>
                            </Grid>
                            <Grid item lg={10} md={10} sm={10} xs={10}>
                              <TextField
                                select
                                size="small"
                                className={classes.textField}
                                defaultValue=""
                                InputLabelProps={{ shrink: true }}
                                {...formik.getFieldProps("teamPositionId")}
                              >
                                <MenuItem
                                  value=""
                                  disabled
                                  style={{ color: "gray" }}
                                >
                                  Select an option
                                </MenuItem>
                                {positionList &&
                                  positionList.map((department) => (
                                    <MenuItem
                                      key={department.id}
                                      value={department.id}
                                    >
                                      {department.positionName}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>
                            <Grid
                              item
                              lg={2}
                              md={2}
                              sm={2}
                              xs={2}
                              display="flex"
                              sx={{
                                justifyContent: "flex-end",
                                alignItems: "flex-end",
                                textAlign: "end",
                              }}
                            >
                              <Button
                                variant="contained"
                                className={classes.editButton}
                                onClick={() => {
                                  setOpenPosition(true);
                                }}
                              >
                                <AddIcon />
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  {/* {formik.values.availability.map((day, index) => (
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Card className={classes.card}>
                        <Grid
                          container
                          justifyContent={"space-between"}
                          spacing={5}
                          sx={{ padding: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            pt={2}
                            alignContent={"center"}
                          >
                            <Typography className={classes.cardTitle}>
                              {day.day} Schedule
                            </Typography>
                          </Grid>
                        </Grid>

                        <Grid container spacing={3} p={"24px"}>
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            key={day.day}
                          >
                            <Grid container spacing={2} alignItems="center">
                              <FieldArray
                                name={`availability.${index}.timeSlots`}
                              >
                                {({ push, remove }) => (
                                  <>
                                    <Grid item lg={12} md={12} sm={12} xs={12}>
                                      <Grid
                                        container
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                      >
                                        <TableContainer>
                                          <Table>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                >
                                                  Start Time
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                >
                                                  End Time
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                ></TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                >
                                                  Hourly Salary
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                  sx={{ textAlign: "right" }}
                                                >
                                                  Action
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {formik.values.availability[
                                                index
                                              ].timeSlots.map(
                                                (slot, slotIndex) => (
                                                  <TableRow key={slotIndex}>
                                                    <TableCell>
                                                      <TextField
                                                        placeholder="Start Time"
                                                        size="small"
                                                        type="time"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.startTime`
                                                        )}
                                                        className={
                                                          classes.textField
                                                        }
                                                        InputProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <TextField
                                                        placeholder="End Time"
                                                        size="small"
                                                        type="time"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.endTime`
                                                        )}
                                                        className={
                                                          classes.textField
                                                        }
                                                        InputProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <FormControlLabel
                                                        control={
                                                          <Checkbox
                                                            // checked={ Boolean(`availability.${index}.timeSlots.${slotIndex}.spansNextDay`)}
                                                            checked={
                                                              formik.values
                                                                .availability[
                                                                index
                                                              ].timeSlots[
                                                                slotIndex
                                                              ].spansNextDay ||
                                                              false
                                                            }
                                                            onChange={(e) =>
                                                              formik.setFieldValue(
                                                                `availability.${index}.timeSlots.${slotIndex}.spansNextDay`,
                                                                e.target.checked
                                                              )
                                                            }
                                                          />
                                                        }
                                                        label="Ends next day"
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <TextField
                                                        placeholder="Hourly Salary"
                                                        size="small"
                                                        type="number"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.hourlySalary`
                                                        )}
                                                        className={
                                                          classes.textField
                                                        }
                                                        InputProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        spacing={1}
                                                        sx={{
                                                          display: "flex",
                                                          justifyContent:
                                                            "flex-end",
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <IconButton
                                                            color="error"
                                                            onClick={() =>
                                                              handleDeleteClick(
                                                                index,
                                                                slotIndex
                                                              )
                                                            }
                                                            // disabled={
                                                            //   formik.values
                                                            //     .availability[
                                                            //     index
                                                            //   ].timeSlots
                                                            //     .length === 1
                                                            // }
                                                          >
                                                            <FaTrash />
                                                          </IconButton>
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </TableRow>
                                                )
                                              )}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Grid>
                                    </Grid>

                                    <Grid
                                      item
                                      lg={12}
                                      md={12}
                                      sm={12}
                                      xs={12}
                                      display="flex"
                                      sx={{
                                        justifyContent: "flex-end",
                                        alignItems: "flex-end",
                                        textAlign: "end",
                                      }}
                                    >
                                      <Button
                                        variant="contained"
                                        // startIcon={<AddIcon />}
                                        className={classes.addButton}
                                        onClick={() =>
                                          push({
                                            id: null,
                                            startTime: "",
                                            endTime: "",
                                            spansNextDay: false,
                                            hourlySalary:
                                              formik.values.teamHourlySalary,
                                          })
                                        }
                                      >
                                        <AddIcon />
                                      </Button>
                                    </Grid>
                                  </>
                                )}
                              </FieldArray>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  ))} */}

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SaveAltIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
                {openDepartment && (
                  <NewDepartement
                    handleCloseDialog={() => setOpenDepartment(false)}
                    openModel={openDepartment}
                  />
                )}

                {openPosition && (
                  <NewPosition
                    handleCloseDialog={() => setOpenPosition(false)}
                    openModel={openPosition}
                  />
                )}

                <Dialog
                  open={openDialog}
                  onClose={handleDeleteCancel}
                  sx={{ padding: 5, minWidth: 450 }}
                >
                  <Grid className={classes.Dialog}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Delete Time Slot
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <DialogContentText sx={{ pt: 3 }}>
                      Are you sure you want to delete this time slot?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions
                    sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}
                  >
                    <Grid container spacing={3}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleDeleteCancel}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<Delete />}
                          className={classes.addButton}
                          onClick={handleDeleteConfirm}
                        >
                          Confirm
                        </Button>
                      </Grid>
                    </Grid>
                  </DialogActions>
                </Dialog>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
